/**
 * XLSX reading — shared strings, sheets, and the Excel date epoch.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3).
 *
 * ⚠ Uses `DOMParser`, a browser API. That is why this lives in `io/` and not
 * `domain/`: it cannot run in a Deno Edge Function, so nothing that needs to be
 * isomorphic may depend on it. Tests supply a DOM via happy-dom.
 *
 * Rewriting it against a Node XML library was the alternative and was rejected:
 * an XLSX parser is exactly the kind of code where a subtly different
 * whitespace or namespace behaviour produces a shifted column rather than an
 * error.
 */

/** Cell values are always strings at this layer — typing happens later. */
export type SheetRow = Record<string, string>;

/**
 * `sharedStrings.xml` → an index-addressable array.
 *
 * A shared string can be split across several `<t>` runs (rich text, or Excel
 * splitting on formatting). They are CONCATENATED — taking only the first run
 * silently truncates any header or symbol that happens to be styled.
 */
export function parseXlsxSharedStrings(xml: string | Uint8Array | null | undefined): string[] {
  if (!xml) return [];
  const text = typeof xml === 'string' ? xml : new TextDecoder().decode(xml);
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  const out: string[] = [];
  for (const si of Array.from(doc.getElementsByTagNameNS('*', 'si'))) {
    let s = '';
    for (const t of Array.from(si.getElementsByTagNameNS('*', 't'))) s += t.textContent || '';
    out.push(s);
  }
  return out;
}

/**
 * One worksheet → rows keyed by COLUMN LETTER.
 *
 * Column letters rather than positions, deliberately: Excel omits empty cells
 * entirely, so a positional read shifts every value after the first gap. This
 * is the single most likely way an importer silently mis-columns a file.
 *
 * Cell types handled: `s` shared string, `inlineStr`, `b` boolean, everything
 * else raw (numbers and date serials arrive as strings and are converted later).
 */
export function parseXlsxSheet(
  xml: string | Uint8Array,
  sharedStrings: readonly string[],
): SheetRow[] {
  const text = typeof xml === 'string' ? xml : new TextDecoder().decode(xml);
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  const rows: SheetRow[] = [];

  for (const rowEl of Array.from(doc.getElementsByTagNameNS('*', 'row'))) {
    const row: SheetRow = {};
    for (const cell of Array.from(rowEl.getElementsByTagNameNS('*', 'c'))) {
      const ref = cell.getAttribute('r') || '';
      const colLetter = (ref.match(/^[A-Z]+/) || [''])[0];
      const t = cell.getAttribute('t');
      const vEl = cell.getElementsByTagNameNS('*', 'v')[0];
      const isEl = cell.getElementsByTagNameNS('*', 'is')[0];

      let val = '';
      if (t === 's') {
        const idx = parseInt(vEl?.textContent || '0', 10);
        val = sharedStrings[idx] || '';
      } else if (t === 'inlineStr') {
        val = isEl?.getElementsByTagNameNS('*', 't')[0]?.textContent || '';
      } else if (t === 'b') {
        val = vEl?.textContent === '1' ? 'TRUE' : 'FALSE';
      } else {
        val = vEl?.textContent || '';
      }
      if (colLetter) row[colLetter] = val;
    }
    rows.push(row);
  }
  return rows;
}

/**
 * Column-letter rows → header-keyed objects, using row 0 as the header.
 *
 * A column with a blank header is DROPPED. Broker exports routinely carry
 * spacer columns, and keying them by letter would leak `A`/`B` pseudo-headers
 * into downstream `getCol` lookups.
 */
export function xlsxRowsToObjects(rows: readonly SheetRow[]): SheetRow[] {
  if (!rows.length) return [];
  const headers = rows[0] as SheetRow;
  const out: SheetRow[] = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i] as SheetRow;
    const obj: SheetRow = {};
    for (const col in r) {
      const h = headers[col];
      if (h) obj[h] = r[col] as string;
    }
    out.push(obj);
  }
  return out;
}

export interface ExcelDateParts { date: string; time: string }

/**
 * Excel serial → `{ date, time }` where time is **`HH:MM:SS`**.
 *
 * ⚠ The epoch is **1899-12-30**, not 1899-12-31 or 1900-01-01. Excel
 * deliberately reproduces a Lotus 1-2-3 bug that treats 1900 as a leap year, and
 * the -30 epoch is what cancels it out. Getting this wrong shifts every imported
 * trade by a day or two — a shift that looks like a timezone problem and gets
 * "fixed" in the wrong place.
 *
 * ⚠ SECONDS ARE PRESERVED. ATAS serials carry sub-minute precision, and
 * truncating to `HH:MM` caused the MFE-zero / same-minute-clamp bug that the
 * `parseTimeOnly` fix solved on the CSV path. Both paths emit `HH:MM:SS` so the
 * excursion adapter gets second-level resolution regardless of import source.
 * (My first draft of this function dropped them — the same regression, two
 * files after porting the fix for it.)
 *
 * ⚠ Date and time are derived SEPARATELY — the date by stepping `days` forward
 * from the epoch in UTC, the time from `frac` alone. Deriving them from one
 * millisecond sum would drag local-timezone conversion into a value that is
 * already timezone-free.
 *
 * The one case where they must interact is the rounding overflow: `frac` in the
 * last half-second of a day rounds to a full 86,400, which has to carry into the
 * date rather than render as "24:00:00". Handled explicitly below.
 */
export function excelSerialToParts(serial: number): ExcelDateParts | null {
  if (typeof serial !== 'number' || !isFinite(serial)) return null;
  const days = Math.floor(serial);
  const frac = serial - days;
  const pad = (n: number) => String(n).padStart(2, '0');

  const base = new Date(Date.UTC(1899, 11, 30));
  base.setUTCDate(base.getUTCDate() + days);

  // ⚠ CARRY THE ROUNDING INTO THE DATE. `Math.round(frac * 86400)` returns
  // 86400 for any frac ≥ 86399.5/86400 — the last half-second of a day — which
  // produced the impossible clock "24:00:00". It does not throw and it does not
  // fail to parse (`new Date('2026-07-20 24:00:00')` is the 21st at midnight),
  // so it flows on as a real timestamp on the WRONG DAY, and it makes
  // sessionFromTimeRaw return '' which suppresses the 18:00 session roll.
  //
  // NOT `Math.floor`: real ATAS serials carry sub-second precision (224 of 284
  // rows in the one real export are off an exact second), so flooring would
  // change the seconds on most rows — a far larger divergence than the bug.
  let totalSec = Math.round(frac * 86_400);
  if (totalSec >= 86_400) {
    totalSec -= 86_400;
    base.setUTCDate(base.getUTCDate() + 1);
  }
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;

  return {
    date: `${base.getUTCFullYear()}-${pad(base.getUTCMonth() + 1)}-${pad(base.getUTCDate())}`,
    time: `${pad(h)}:${pad(m)}:${pad(s)}`,
  };
}
